/**
 * @module @sm/intl
 */

import React, { createContext } from 'react';
import PropTypes from 'prop-types';
import IntlMessageFormat from 'intl-messageformat';
import escapeHtml from 'escape-html';
import invariant from 'invariant';
import merge from 'lodash.merge';
import { getClientEnvironmentDetails } from '@sm/utils';
import { LANGUAGES } from '@sm/locale';
import IntlRelativeFormat from 'intl-relativeformat';
import defaultFormats from './formats';

const { isBrowser } = getClientEnvironmentDetails();

const INTL_OPT_KEY = 'SM_intlUniqueOptionsObjectKey';
const INTL_MSG_STORE_KEY = 'SM_intlUniqueMessageStoreObjectKey';

/** Intl Options */
export type Options = {
  /** ISO-Compliant locale code (i.e. `fr-FR`, `en-US` or `zh-CN`) */
  currentLocale: string;
  /** ISO 639-1 Compliant 2-letter language code (i.e. `fr`, `en` or `zh`) */
  currentLanguage?: string;
  warningHandler: (...args: any[]) => void;
  escapeHtml: boolean;
  /** formats object passed to the Intl API constructors */
  formats?: {};
};

type LocaleData = {
  locale: string;
  parentLocale?: string;
  fields?: any;
};

/** `{[local]: {[msgKey]: msgValue}}` */
export type MessageStore = Record<string, Record<string, string>>;

export type VariableMap = Record<string, React.ReactNode | JSX.Element> | null;

type MessageType = string | (string | React.ReactElement)[];

/** Internal Variables on the Global/Window scope */
type GlobalVars = {
  SM_intlUniqueOptionsObjectKey?: Options;
  SM_intlUniqueMessageStoreObjectKey?: MessageStore;
  ReactIntlLocaleData: Record<string, LocaleData[]>;
};

// ambient-type global variables on `Window` and `Global`
declare global {
  interface Window extends GlobalVars {}
  const __DEV__: string | undefined;
}

// See https://github.com/microsoft/TypeScript/issues/30139
// eslint-disable-next-line @typescript-eslint/no-namespace
declare module global {
  // eslint-disable-next-line camelcase
  const SM_intlUniqueOptionsObjectKey: Options | undefined;
  // eslint-disable-next-line camelcase
  const SM_intlUniqueMessageStoreObjectKey: MessageStore | undefined;
  const ReactIntlLocaleData: Record<string, LocaleData[]>;
}

function getDefaultOptions(): Options {
  return {
    currentLocale: null as any, // Current locale such as 'en-US' - `null` is not allowed for IntlMessageFormat and needs to be set
    warningHandler: console.warn.bind(console), // eslint-disable-line
    escapeHtml: true, // disable escape html in variable mode
  };
}

type Mutable<T> = {
  -readonly [K in keyof T]: T[K];
};

function getStore(): MessageStore {
  const glob = isBrowser ? window : (global as Mutable<GlobalVars>);
  if (!glob[INTL_MSG_STORE_KEY]) {
    glob[INTL_MSG_STORE_KEY] = {};
  }
  return glob[INTL_MSG_STORE_KEY] as MessageStore;
}

function getOptionsObject(): Options {
  const glob = isBrowser ? window : (global as Mutable<GlobalVars>);
  if (!glob[INTL_OPT_KEY]) {
    glob[INTL_OPT_KEY] = getDefaultOptions();
  }
  return glob[INTL_OPT_KEY] as Options;
}

export function clearOptionsObject() {
  if (isBrowser) {
    delete window[INTL_OPT_KEY];
  } else {
    delete (global as Mutable<GlobalVars>)[INTL_OPT_KEY];
  }
}

let intlRelativeFormat: IntlRelativeFormat;

/**
 * @param {string} input
 * @returns {string} hash
 * @private
 */
function hashInput(input: string) {
  let hash = 0;
  let i;
  let chr;
  if (input.length === 0) return hash;
  for (i = 0; i < input.length; i += 1) {
    chr = input.charCodeAt(i);
    hash = (hash << 5) - hash + chr; // eslint-disable-line no-bitwise
    // Convert to 32bit integer
    hash |= 0; // eslint-disable-line no-bitwise
  }
  return hash;
}

const randomStr = (N: string | number) =>
  [...Array(N)].map(() => (~~(Math.random() * 36)).toString(36)).join(''); // eslint-disable-line no-bitwise

const keysOf = Object.keys;

const maybePseudo = (msg: string) => {
  if (__DEV__ && isBrowser) {
    const pseudoFactor = window.location.href
      .toString()
      .match(/[&?]intl-pseudo(=([0-9]*\.[0-9]+|[0-9]+))?/);
    if (pseudoFactor) {
      return msg.replace(/\S+/g, match =>
        randomStr(
          parseInt(
            `${match.length * parseFloat(`${pseudoFactor[2] || 1}`)}`,
            10
          ) || 1
        )
      );
    }
  }
  return msg;
};

const replacer =
  <T extends string>(elements: Record<T, React.ReactElement>) =>
  (part: T): React.ReactElement | string => {
    const element = elements[part];
    return element || maybePseudo(part);
  };

/** `string` type-guard including check for `instanceof` */
// work-around as  TS does not infer type string for instanceof
const isString = (s: unknown): s is string =>
  typeof s === 'string' || s instanceof String;

/**
 *
 * @param {string} message
 * @param {Object} variables (Optional)
 * @returns {Object} The substitution
 */
export function substitute(
  message: string,
  variables?: VariableMap
): MessageType {
  const vars = { ...variables };
  let msg: MessageType = message;
  const options = getOptionsObject();

  const uid = Math.abs(
    hashInput(JSON.stringify({ msg, keys: keysOf(vars) }))
  ).toString(16);
  const tokenDelimiter = `@__${uid}__@`;
  const tokenizedValues: Record<
    string,
    Exclude<React.ReactNode, React.ReactElement>
  > = {};
  const elements: Record<string, React.ReactElement> = {};

  const generateToken = (() => {
    let counter = 0;
    return () => {
      counter += 1;
      return `ELEMENT-${uid}-${counter}`;
    };
  })();

  keysOf(vars).forEach(name => {
    const value = vars[name];
    if (React.isValidElement(value)) {
      const token = generateToken();
      tokenizedValues[name] = tokenDelimiter + token + tokenDelimiter;
      elements[token] = value;
    } else if (
      options.escapeHtml === true &&
      isString(value) &&
      value.indexOf('<') >= 0 &&
      value.indexOf('>') >= 0
    ) {
      tokenizedValues[name] = escapeHtml(value);
    } else {
      // `React.isValidElement` matches `React.ReactElement` so its type can be omitted
      tokenizedValues[name] = value as Exclude<
        React.ReactNode,
        React.ReactElement
      >;
    }
  });

  try {
    const msgFormatter = new IntlMessageFormat(
      msg,
      options.currentLocale,
      options.formats
    );
    msg = msgFormatter.format(tokenizedValues);
  } catch (err) {
    options.warningHandler({
      context: 'sm/intl',
      error: 'message_formatting_error',
      err,
      msg,
      tokenizedValues,
      vars,
      currentLocale: options.currentLocale,
    });
    return msg;
  }

  try {
    const stack: { tag: string; index: number }[] = [];
    let exhausted = false;
    let runningIndex = 0;

    while (!exhausted) {
      // The runningIndex is the pointer in the string at which we want to look right (->).
      const match: RegExpMatchArray | null = msg
        .substr(runningIndex)
        .match(/<span id="([^<>]+)">|<\/span>/);

      if (match) {
        // eslint-disable-next-line prefer-const
        let [matched, tag] = match as [string, string];

        // we need to adjust for the running index because match.index is relative to the substring we used
        const matchIndex: number = (match.index || 0) + runningIndex;
        const isOpening = !!tag;
        if (isOpening) {
          // If it's an opening tag, just push it on the stack and move on.
          stack.push({ tag, index: matchIndex });
          runningIndex = matchIndex + matched.length;
        } else {
          // We reached an ending </span> tag.

          const popped = stack.pop();
          if (!popped) {
            options.warningHandler({
              context: 'sm/intl',
              error: 'unbalanced_delimiters_extra_closing',
              message,
              tokenizedValues,
              vars,
            });
            return message;
          }
          runningIndex = popped.index;
          // Find the content between the opening tag and the current one
          const content = msg
            .substring(runningIndex, matchIndex)
            .replace(/<span id="([^<>]+)">/, '')
            .replace(/<\span>/, '');

          let children: MessageType;
          // If there are other functional elements saved already, try to split
          // the content and replace them with the right elements.
          if (elements && keysOf(elements).length > 0) {
            children = content
              .split(tokenDelimiter)
              .filter(part => !!part)
              .map(replacer(elements));
          } else {
            children = content;
          }

          // Build an element by cloning the passed element or calling the
          // passed function.
          let element = (vars[popped.tag] as React.FunctionComponent)({
            children,
          }) as React.ReactElement;
          if (React.isValidElement(element)) {
            element = React.cloneElement(element, {
              key: `${popped.tag}_${randomStr(8)}`,
            });
          }

          // Build the new string and adjust the running index based on that token.
          const token = generateToken();
          const tokenReplacement = `${tokenDelimiter}${token}${tokenDelimiter}`;
          msg = `${msg.substring(
            0,
            popped.index
          )}${tokenReplacement}${msg.substring(
            matchIndex + matched.length,
            msg.length
          )}`;
          elements[token] = element;
        }
      } else {
        exhausted = true;
      }
    }
    if (stack.length) {
      options.warningHandler({
        context: 'sm/intl',
        error: 'unbalanced_delimiters_extra_opening',
        message,
        tokenizedValues,
        vars,
      });
      return message;
    }
    // Do a final replace once we're done with matching
    if (elements && keysOf(elements).length > 0) {
      msg = msg
        .split(tokenDelimiter)
        .filter(part => !!part)
        .map(replacer(elements));
    } else {
      msg = maybePseudo(msg);
    }

    return msg;
  } catch (err) {
    options.warningHandler({
      context: 'sm/intl',
      error: 'unexpected_tokenization_error',
      message,
      err,
    });
    return message;
  }
}

/**
 * Retrieves and format a particular message by key. Uses defaultMessage if key is not found in message list.
 * @param {string} key Unique identifier for this string
 * @param {Object} variables (Optional) - Key/values for string substitution
 * @param {string} defaultMessage (Optional) Default message if string isn't found for key
 * @private
 */
function get(
  key: string,
  variables?: VariableMap,
  defaultMessage?: string
): MessageType {
  const vars = variables;
  const { currentLocale, warningHandler } = getOptionsObject();
  const messages = getStore()[currentLocale];
  if (!key) {
    warningHandler({
      context: 'sm/intl',
      error: 'key_is_undefined',
      currentLocale,
      key,
      defaultMessage,
    });
    return '';
  }

  let msg: string | undefined;
  if (!messages) {
    // If currentLocale is not set, return the default message without warning.
    if (currentLocale) {
      warningHandler({
        context: 'sm/intl',
        error: 'locale_not_found_in_message_store',
        currentLocale,
      });
    }
    msg = defaultMessage || '';
  }

  msg = msg || messages[key];

  if (!msg && defaultMessage) {
    if (currentLocale !== 'en-US') {
      warningHandler({
        context: 'sm/intl',
        error: 'message_not_found_default_fallback',
        currentLocale,
        key,
      });
    }
    msg = defaultMessage;
  }

  if (!msg) {
    warningHandler({
      context: 'sm/intl',
      error: 'message_not_found_no_default',
      currentLocale,
      key,
      defaultMessage,
    });
    return '';
  }
  return substitute(msg, vars);
}

/**
 * @param {string} key
 * @param {Object} variables
 * @param {string} defaultMessage
 * @returns {string} html
 * @private
 */
function getHTML(
  key: string,
  variables?: VariableMap,
  defaultMessage?: string
): React.ReactChild {
  const msg = get(key, variables, defaultMessage);
  if (msg) {
    const el = React.createElement('span', {
      dangerouslySetInnerHTML: {
        __html: msg,
      },
    });
    // when key exists, it should still return element if there's defaultMessage() after getHTML()
    const defaultMsg = () => el;
    return {
      defaultMessage: defaultMsg,
      d: defaultMsg,
      ...el,
    } as React.ReactElement;
  }
  return '';
}

/**
 *
 * @param {string} currentLocale
 * @param {string} currentLanguage
 * @private
 */
function defineIntlRelativeFormat(
  currentLocale: string,
  currentLanguage: string
) {
  if (
    window.ReactIntlLocaleData &&
    window.ReactIntlLocaleData[currentLanguage] &&
    Object.keys(window.ReactIntlLocaleData[currentLanguage]).length > 0
  ) {
    window.ReactIntlLocaleData[currentLanguage].forEach(localeData => {
      (
        IntlRelativeFormat as typeof IntlRelativeFormat & {
          // internal private properties not in the ambient types
          __addLocaleData: (localeData: LocaleData) => void;
        }
      ).__addLocaleData(localeData);
    });
  }
  intlRelativeFormat = new IntlRelativeFormat(currentLocale, {
    ...(defaultFormats.relativeTime || {}),
  });
}

// =================== Public API

export type Desc<
  ID extends string = string,
  MSG extends string = string,
  DESC extends string = string
> = {
  /** Globally unique ID identifying this message,
   *
   * should follow the format `<PageName>.<ComponentName>.<UniqueId>` for pages or `<ComponentName>.<UniqueId>` for components
   *
   * @example
   * "apps/respweb/src/app/pages/SurveyClosed/BrandedSurveyClosed/index.js" -> "SurveyClosed.BrandedSurveyClosed.EndpageHeaderCopy"
   *
   * "apps/respweb/src/app/components/Survey/PageHeader/index.js" -> "PageHeader.Page"
   */
  id: ID;
  /** Actual message following the ICU message format
   *
   * - Supports plural, select, and selectordinal message arguments.
   * - Formats numbers and dates/times in messages using Intl.NumberFormat and Intl.DateTimeFormat, respectively.
   *
   * - {@link https://formatjs.io/docs/core-concepts/icu-syntax|Format.JS Docs}
   * - {@link http://userguide.icu-project.org/formatparse/messages|ICU Docs}
   * - {@link https://format-message.github.io/icu-message-format-for-translators/editor.html|ICU Online editor}
   */
  defaultMessage?: MSG;
  /**
   * Description provided to the translators.
   *
   * Type must have one of the following values `Button | Link | Label | Header | Paragraph`. Vis, for visibility, must be one of `low | med | high`
   *
   * Strings with high visibility impact the decision a user makes using our product. Usually these are strings that change the workflow or setting of a property. Most likely all Call to Action (CTA) elements are `high` visibility. Tool tips, page title and help content tend to be `low` visibility. If unsure please set the block to `medium` visibility.
   *
   * In general we pay more attention to strings with `high` visibility during the translation, editing and review phases.
   */
  description?: DESC;
};

/**
 * Definition function used by our babel-plugin to extract messages during development.
 * For the description, Type must have one of the following values `Button | Link | Label | Header | Paragraph`. Vis, for visibility, must be one of `low | med | high`.
 *
 * More in the {@link https://code.corp.surveymonkey.com/pages/webplatform/smweb/#/pages/webs/localization?id=definemessages|WebPlatform docs}
 *
 * @param {Object} v Key/Descriptor objects where Descriptor.id, Descriptor.defaultMessage and Descriptor.description are present.
 * @returns {Object} message definition
 *
 */
export function defineMessages<
  T extends Record<string, Desc<ID, M, D>>,
  ID extends string,
  M extends string,
  D extends string
>(v: T): T {
  return v;
}

/**
 * Translates a message matching a specific Descriptor object.
 * Ref: https://code.corp.surveymonkey.com/pages/webplatform/smweb/#/pages/webs/localization
 *
 * @param {Desc} desc Descriptor object for the current message to translate. ({id: String!, defaultMessage: String!, description: String})
 * @param {Object} values (Optional) - key/values for substitution based on the ICU message format.
 * @param {Object} opts (Optional)
 * @param {Object} opts.html (Optional/Defaults to `false`) - Whether or not to allow for HTML nodes in the message before substitution.
 * @returns {string|React.ReactElement} HTML string or key value
 */
export function t<
  IS_HTML extends boolean = false,
  T extends React.ReactNode = undefined,
  R = IS_HTML extends true
    ? React.ReactElement
    : T extends string | number | boolean | undefined | null
    ? string // for non-object primitives
    : React.ReactElement
>(
  desc: Desc,
  values?: Record<string, T> | null,
  opts: { html?: IS_HTML } = {}
): NonNullable<R> {
  const { id, defaultMessage } = desc;
  const { html } = opts;
  return (html ? getHTML : get)(
    id,
    values,
    defaultMessage
  ) as unknown as NonNullable<R>; // force return type
}

/** Props for `T` React component */
export type TProps = {
  id: string;
  /** Descriptor object for the current message to translate. */
  desc?: Desc;
  /**
   * key/values for substitution based on the ICU message format.
   *
   * - Supports plural, select, and selectordinal message arguments.
   * - Formats numbers and dates/times in messages using Intl.NumberFormat and Intl.DateTimeFormat, respectively.
   *
   * - {@link https://formatjs.io/docs/core-concepts/icu-syntax|Format.JS Docs}
   * - {@link http://userguide.icu-project.org/formatparse/messages|ICU Docs}
   */
  values?: VariableMap;
  defaultMessage?: string;
  /** (Optional/Defaults to `false`) - Whether or not to allow for HTML nodes in the message before substitution. */
  html?: boolean;
};

/**
 * (React Component) Translates a message matching a specific Descriptor object.
 * Ref: https://code.corp.surveymonkey.com/pages/webplatform/smweb/#/pages/webs/localization
 */
export const T = (props: TProps): React.ReactElement => {
  const { desc, values, id, defaultMessage, html } = props;
  return (html ? getHTML : get)(
    desc ? desc.id : id,
    values,
    desc ? desc.defaultMessage : defaultMessage
  ) as React.ReactElement;
};

T.propTypes = {
  desc: PropTypes.shape({
    id: PropTypes.string.isRequired,
    defaultMessage: PropTypes.string.isRequired,
    description: PropTypes.string,
  }),
  id: PropTypes.string,
  // PropTypes.node does not include `true`
  // (see https://github.com/facebook/prop-types/issues/199)
  values: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.node, PropTypes.bool])
  ),
  defaultMessage: PropTypes.string,
  html: PropTypes.bool,
};

T.defaultProps = {
  desc: undefined,
  id: undefined,
  values: undefined,
  defaultMessage: undefined,
  html: false,
};

/**
 * Initialize the localization framework with the passed messages and set of currentLocale/currentLanguage. Can be called more than once.
 * @param {Object} messages Key/Values where keys are string IDs and values are the translated message for such ID.
 * @param {Options} [opts={}] Options for Intl
 * @returns {void}
 */
export function initIntl(
  /** Key/Values where keys are string IDs and values are the translated message for such ID */
  messages: Record<string, string>,
  opts: Partial<Options> = {}
): void {
  invariant(opts.currentLocale, 'opts.currentLocale is required');
  invariant(opts.currentLanguage, 'opts.currentLanguage is required');
  invariant(messages, 'messages is required');
  const options = getOptionsObject();
  options.currentLocale = opts.currentLocale;
  options.currentLanguage = opts.currentLanguage;
  options.warningHandler = opts.warningHandler || options.warningHandler;
  const messageStore = getStore();
  const langInited = !!messageStore[options.currentLocale];

  merge(messageStore, { [options.currentLocale]: messages });

  options.formats = {
    ...options.formats,
    ...opts.formats,
    ...defaultFormats,
  };
  if (langInited) {
    return;
  }

  if (isBrowser) {
    defineIntlRelativeFormat(options.currentLocale, options.currentLanguage);
  } else {
    // For Node.js, common locales are added in the application
    intlRelativeFormat = new IntlRelativeFormat(options.currentLocale, {
      ...(defaultFormats.relativeTime || {}),
    });
  }
}

/**
 * Clears the object where we store messages. Useful for testing
 * @returns {undefined}
 */
export function resetMessageStore(): void {
  const messageStore = getStore();
  Object.keys(messageStore).forEach(member => delete messageStore[member]);
}

/**
 * Retrieves a copy of the stored messages.
 * @returns {JSON} JSON Message Store
 */
export function getMessageStore(): MessageStore {
  return JSON.parse(JSON.stringify(getStore()));
}

export type FormattedDateTimeProps = {
  value?: number | Date;
} & Partial<Intl.DateTimeFormatOptions>;

/**
 * The FormattedDateTime component uses the Intl.DateTimeFormat API and as such, here are the supported props.
 * All optionals.
 *
 * type DateTimeFormatOptions = {
 *   localeMatcher: 'best fit' | 'lookup' = 'best fit',
 *   formatMatcher: 'basic' | 'best fit' = 'best fit',
 *
 *   timeZone: string,
 *   hour12  : boolean,
 *
 *   weekday     : 'narrow' | 'short' | 'long',
 *   era         : 'narrow' | 'short' | 'long',
 *   year        : 'numeric' | '2-digit',
 *   month       : 'numeric' | '2-digit' | 'narrow' | 'short' | 'long',
 *   day         : 'numeric' | '2-digit',
 *   hour        : 'numeric' | '2-digit',
 *   minute      : 'numeric' | '2-digit',
 *   second      : 'numeric' | '2-digit',
 *   timeZoneName: 'short' | 'long',
 * };
 *
 * @param {Date} props Actual date to format.
 * @returns {string} formatted date
 */
export function FormattedDateTime(
  props: FormattedDateTimeProps
): React.ReactElement {
  const { value, ...restProps } = props;
  const options = getOptionsObject();
  return new Intl.DateTimeFormat(options.currentLocale ?? undefined, {
    ...(defaultFormats.dateTime || {}),
    ...restProps,
  }).format(value) as unknown as React.ReactElement;
}

export type FormattedNumberProps = {
  /** Actual number to format. */
  value: number;
  /** defaults to `decimal` - overwrites `style` if both are set */
  formatStyle?: 'decimal' | 'currency' | 'percent' | 'unit';
  /** defaults to `symbol` */
  currencyDisplay?: 'symbol' | 'code' | 'name';
  /** defaults to `best fit` */
  localeMatcher?: 'best fit' | 'lookup';
} & Omit<Intl.NumberFormatOptions, 'localeMatcher' | 'currencyDisplay'>;

/**
 * The FormattedNumber component uses the Intl.NumberFormat API and as such, here are the supported props.
 * All optionals.
 *
 * type NumberFormatOptions = {
 *   localeMatcher: 'best fit' | 'lookup' = 'best fit',
 *
 *   style: 'decimal' | 'currency' | 'percent' = 'decimal',
 *
 *   currency       : string,
 *   currencyDisplay: 'symbol' | 'code' | 'name' = 'symbol',
 *
 *   useGrouping: boolean = true,
 *
 *   minimumIntegerDigits    : number = 1,
 *   minimumFractionDigits   : number,
 *   maximumFractionDigits   : number,
 *   minimumSignificantDigits: number = 1,
 *   maximumSignificantDigits: number,
 * };
 *
 * @param {number} props Actual number to format.
 * @returns {string} Formatted number
 */
export function FormattedNumber(
  props: FormattedNumberProps
): React.ReactElement {
  const { value, ...restProps } = props;
  const options = getOptionsObject();
  restProps.style = restProps.formatStyle;
  return new Intl.NumberFormat(options.currentLocale ?? undefined, {
    ...(defaultFormats.number || {}),
    ...restProps,
  }).format(value) as unknown as React.ReactElement;
}

export type FormattedRelativeTimeProps = {
  /** Actual date to format. */
  value: Date;
  /** Fix the value of "now" to a certain date. */
  now?: Date;
};

/**
 * The FormattedRelativeTime component uses the IntlRelativeTime API and as such, here are the supported props.
 * All optionals.
 *
 * type RelativeTimeFormatOptions = {
 *   units: 'second' | 'second-short' | 'minute' | 'minute-short' | 'hour' | 'hour-short' | 'day' | 'day-short' | 'month' | 'month-short' | 'year' | 'year-short'
 *   style: 'best fit' | 'numeric'
 * };
 *
 * @param {Object} props
 * @param {Date} props.value Actual date to format.
 * @param {Date} props.now (Optional) Fix the value of "now" to a certain date.
 * @returns {string} Formatted date
 */
export function FormattedRelativeTime(
  props: FormattedRelativeTimeProps
): React.ReactElement {
  const { value, now } = props;
  return intlRelativeFormat.format(value, {
    now,
  }) as unknown as React.ReactElement;
}

/** Context is currently only used to prevent isRTL prop drilling */
export const L10NContext = createContext({ isRTL: false });
const RTLLanguageCodes = LANGUAGES.filter(l => l.isRTL).map(l => l.code);

export type L10nProviderProps = {
  /** ISO 639-1 Compliant 2-letter language code (i.e. `fr`, `en` or `zh`) */
  languageCode: string;
} & (
  | {
      /** ISO-Compliant locale code (i.e. `fr-FR`, `en-US` or `zh-CN`) */
      localeCode: string;
      /** Messages - ignored for `localeCode="en-US"` */
      localeMessages: Record<string, string>;
    }
  | {
      localeCode: 'en-US';
      /** `en-US` does not need `localeMessages` */
      localeMessages?: Record<string, string>;
    }
);

type L10nProviderState = {
  messages: Record<string, string> | null;
  isRTL: boolean;
};

/**
 * Configures the global localization context for the whole page.
 * @class
 */
export class L10nProvider extends React.Component<
  L10nProviderProps,
  L10nProviderState
> {
  static propTypes = {
    children: PropTypes.node.isRequired,
    localeMessages: PropTypes.object, // eslint-disable-line
    localeCode: PropTypes.string,
    languageCode: PropTypes.string,
  };

  static defaultProps = {
    localeCode: 'en-US',
    languageCode: 'en',
  };

  state: L10nProviderState = {
    messages: null,
    isRTL: false,
  };

  constructor(props: L10nProviderProps) {
    super(props);
    const currentLocale = props.localeCode;
    const currentLanguage = props.languageCode;

    // If the locale is english, we are not expecting any messages; hence, default to {}
    this.state.messages = this.getMessages();
    /**
     * `SSR/isNode` - Initialize Intl on the server with the provided messages (if any).
     * `messages ` - We already have the messages (SSR/CSR/en-US), initIntl right away.
     *
     * The above takes care of three scenarios -
     * 1. SSR - Everything is provided on the server; hence, no need to fetch messages.
     * 2. CSR + messages - Messages were fetched before this component; hence, no need to fetch messages.
     * 3. In the case of `en-US`, we really don't need messages.
     */
    const { isNode } = getClientEnvironmentDetails();
    if (isNode || this.state.messages) {
      initIntl(this.state.messages, {
        currentLocale,
        currentLanguage,
      });
    }
    if (RTLLanguageCodes.indexOf(currentLanguage) !== -1) {
      this.state.isRTL = true;
    }
  }

  componentDidUpdate(prevProps: L10nProviderProps) {
    // Update text-direction
    if (prevProps.languageCode !== this.props.languageCode) {
      const isRTL = RTLLanguageCodes.indexOf(this.props.languageCode) !== -1;
      if (isRTL !== this.state.isRTL) {
        this.setState(() => {
          return { isRTL };
        });
      }
    }

    // Update messages at runtime is not yet implemented
  }

  /** Picks the correct localized messages */
  private getMessages() {
    return this.props.localeCode === 'en-US'
      ? {}
      : this.props.localeMessages || {};
  }

  render() {
    if (!this.state.messages) {
      return null;
    }
    return (
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      <L10NContext.Provider value={{ isRTL: this.state.isRTL }}>
        {this.props.children}
      </L10NContext.Provider>
    );
  }
}
